"use client";

import { useRouter } from "next/navigation";
import { Printer } from "lucide-react";
import Navbar from "@/components/layout/navbar/Navbar";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { TrainingCard } from "@/components/Common/TrainingCard";
import { useBookmarks } from "@/lib/bookmarks";
import { usePrintPage } from "@/hooks/usePrintPage";
import Image from "next/image";

/**
 * Empty state shown when the user hasn't saved any formation yet.
 * Kept as a small presentational sub-component (Single Responsibility).
 */
function EmptyState({ onBrowse }: { onBrowse: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 md:py-24">
      <div className="w-16 h-16 rounded-full bg-[#C7F8FF] flex items-center justify-center mb-6">
        <svg
          width="28"
          height="28"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9 2.875C7.60513 2.875 6.22936 2.9557 4.87706 3.11264C4.38227 3.17006 4 3.60073 4 4.13024V14.7387L8.72049 12.3785C8.89645 12.2905 9.10355 12.2905 9.27951 12.3785L14 14.7387V4.13024C14 3.60073 13.6177 3.17006 13.1229 3.11264C11.7706 2.9557 10.3949 2.875 9 2.875ZM4.73296 1.87097C6.13299 1.7085 7.55687 1.625 9 1.625C10.4431 1.625 11.867 1.7085 13.267 1.87097C14.4231 2.00514 15.25 2.99777 15.25 4.13024V15.75C15.25 15.9666 15.1378 16.1678 14.9536 16.2817C14.7693 16.3955 14.5392 16.4059 14.3455 16.309L9 13.6363L3.65451 16.309C3.46077 16.4059 3.23068 16.3955 3.04642 16.2817C2.86216 16.1678 2.75 15.9666 2.75 15.75V4.13024C2.75 2.99777 3.57685 2.00514 4.73296 1.87097Z"
            fill="#1B365E"
          />
        </svg>
      </div>
      <h2 className="text-[#1B365E] font-bold text-[20px] md:text-[24px] mb-2">
        Aucune formation enregistrée pour le moment.
      </h2>
      <p className="text-[#676767] text-[14px] md:text-[16px] max-w-md mb-6">
        Parcourez les formations et cliquez sur l&apos;icône favoris pour les
        enregistrer.
      </p>
      <button
        type="button"
        onClick={onBrowse}
        className="bg-[#1B365E] cursor-pointer border border-[#1B365E] text-white font-bold px-6 h-11 rounded-lg hover:bg-white hover:text-[#1B365E] transition-colors"
      >
        Découvrir les formations
      </button>
    </div>
  );
}

/**
 * Saved formations page body.
 *
 * Reads the session-scoped bookmark store and renders the saved formations
 * with the exact same {@link TrainingCard} + `.catalog-grid` layout used by the
 * main catalog, so cards look identical to the live listings. Client-only:
 * bookmarks live in sessionStorage.
 */
export default function SavedFormations() {
  const router = useRouter();
  const bookmarks = useBookmarks();
  const print = usePrintPage();
  const count = bookmarks.length;

  return (
    <section>
      {/* Header band — hosts the (absolute) navbar over the brand-navy hero color,
          mirroring how every page's Hero frames the navbar. */}
      {/* Navbar */}
      <div className="relative z-100">
        <Navbar />
      </div>
      <div className="relative w-full bg-[#1B365E] pt-13.5 sm:pt-22 pb-10 md:pb-14 px-4 md:px-7">
        {/* Hero Background */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/formations-hero-bg-0.svg"
            alt="Hero Background"
            fill
            priority
            className="object-cover w-full h-full"
          />
        </div>


        <Breadcrumb withSchema={false} className="relative z-10 left-3.25" />

        {/* Hero Text */}
        <div className="relative z-10 max-w-346.25 mx-auto text-center">
          <h1 className="text-white font-light font-red-hat tracking-[0.2px] text-[30px] md:text-[50px] leading-10 md:leading-19">
            Mes formations enregistrées
          </h1>
          {count > 0 && (
            <p className="text-[#C7F8FF] text-[14px] md:text-[16px] mt-2">
              {count} formation{count > 1 ? "s" : ""} enregistrée
              {count > 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="md:px-7 px-4 py-12 md:py-16">
        <div className="max-w-346.25 mx-auto">
          {count === 0 ? (
            <EmptyState onBrowse={() => router.push("/formations")} />
          ) : (
            <>
              {/* Print action — the page itself is the document, so this just
                  hands it to the browser dialog. `usePrintPage` takes the site
                  footer out of the printout; the button takes itself out. */}
              <div className="mb-6 flex justify-end" data-print-hide>
                <button
                  type="button"
                  onClick={print}
                  title="Imprimer mes formations enregistrées"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#1B365E] px-4 h-10 text-[14px] font-bold text-[#1B365E] hover:bg-[#1B365E] hover:text-white transition-colors cursor-pointer"
                >
                  <Printer className="w-4 h-4" aria-hidden="true" />
                  Imprimer
                </button>
              </div>

              <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4 auto-rows-fr">
                {bookmarks.map((f, index) => (
                  <div
                    key={f.slug}
                    data-print-keep-together
                    className={`w-full h-full justify-self-center ${index % 2 === 0 ? "lg:justify-self-start" : "lg:justify-self-end"} ${index % 3 === 0 ? "xl:justify-self-start" : index % 3 === 1 ? "xl:justify-self-center" : "xl:justify-self-end"}`}
                  >
                    <TrainingCard
                      title={f.title}
                      description={f.description}
                      category={f.category}
                      location={f.location}
                      duration={f.duration}
                      price={f.price}
                      financing={f.financing}
                      isNew={f.isNew}
                      customClass="w-full min-h-full xl:w-71"
                      formation={f}
                      onDiscover={() => router.push(`/formation/${f.slug}`)}
                      onSubscribe={() => router.push(`/formation/${f.slug}`)}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
